<template>
	<v-container
		fluid
		class="d-flex justify-space-between form-page-gutter invoice-form-page flex-wrap flex-md-nowrap flex-lg-nowrap">
		<v-card elevation="0" min-width="50%" class="rounded-xl">
			<v-card-title
				v-if="!isUpdating"
				class="d-flex align-center justify-space-between flex-wrap flex-md-nowrap flex-lg-nowrap">
				<div>New Invoice</div>
				<v-btn
					color="primary"
					depressed
					rounded
					@click="addInvoice"
					:loading="isLoading"
					:disabled="!formValid">
					<v-icon class="mr-2">mdi-file-document</v-icon>
					Add New Invoice
				</v-btn>
			</v-card-title>
			<v-card-title v-else class=" ">
				<v-container
					class="px-0 d-flex align-center justify-space-between flex-wrap flex-md-nowrap flex-lg-nowrap">
					<div>Invoice Detail</div>
					<h3 class="primary--text font-italic font-weight-thin">
						<span>
							{{
								getOptionName(invoice.typeId)[0].toUpperCase()
							}}-{{ String(invoice.id).padStart(5, "0") }}
						</span>

						<span>{{ getUserName(invoice.createdBy) }}</span>
						<span class="ml-1">
							{{ convertToDate(invoice.dateCreated) }}
						</span>
					</h3>
				</v-container>
				<v-container
					class="pa-0 d-flex justify-center justify-md-space-between justify-lg-space-between flex-wrap flex-md-nowrap flex-lg-nowrap"
					style="gap: 5px">
					<v-btn
						v-if="
							isUpdating &&
							invoice.associatedId &&
							invoice.typeId == Shipment
						"
						color="primary"
						depressed
						rounded
						@click="
							$router
								.push({
									name: 'ShipmentUpdate',
									params: { id: invoice.associatedId },
								})
								.catch(() => {})
						">
						<v-icon class="mr-2">mdi-file-document-outline</v-icon>
						Go To Shipment
					</v-btn>
					<v-btn
						v-if="
							isUpdating &&
							invoice.associatedId &&
							invoice.typeId == Order
						"
						color="primary"
						depressed
						rounded
						@click="
							$router
								.push({
									name: 'Shipment.OrderDetail',
									params: { id: invoice.associatedId },
								})
								.catch(() => {})
						">
						<v-icon class="mr-2">mdi-file-document-outline</v-icon>
						Go To Order
					</v-btn>

					<v-btn
						v-if="isUpdating"
						color="warning"
						depressed
						rounded
						@click="updateInvoice"
						:loading="isLoading"
						:disabled="!formValid">
						<v-icon class="mr-2">mdi-file-document</v-icon>
						Update Invoice
					</v-btn>

					<v-btn
						v-if="isUpdating"
						color="primary"
						depressed
						rounded
						:href="`/#/shipment/management/printer/friendly/${invoice.id}`"
						target="_blank">
						<v-icon class="mr-2">mdi-printer</v-icon>
						Printer Friendly
					</v-btn>
					<v-tooltip
						bottom
						v-if="isUpdating && paymentStatusId == Paid">
						<template v-slot:activator="{ on, attrs }">
							<div v-on="on" v-bind="attrs">
								<v-btn
									v-if="isUpdating"
									rounded
									depressed
									color="error"
									:disabled="paymentStatusId == Paid">
									<v-icon>mdi-delete</v-icon>
								</v-btn>
							</div>
						</template>
						<span>Can't delete. Payment has been made</span>
					</v-tooltip>
					<v-btn
						v-if="isUpdating && paymentStatusId != Paid"
						rounded
						depressed
						color="error"
						@click="deleteDialog = true"
						:disabled="paymentStatusId == Paid"
						:loading="isDeleteLoading">
						<v-icon>mdi-delete</v-icon>
					</v-btn>
				</v-container>
			</v-card-title>
			<v-form ref="form" v-model="formValid">
				<v-card-text class="py-0 mb-2">
					<v-container
						class="d-flex"
						style="gap: 5px">
						<v-text-field
							rounded
							outlined
							dense
							label="Tracking #"
							v-model="invoice.mtlTracking"></v-text-field>

						<v-autocomplete
							rounded
							outlined
							dense
							:rules="dropdownRules"
							:items="currencyTypes"
							item-text="displayName"
							item-value="id"
							label="Currency Type"
							v-model.number="currencyId"
							:disabled="paymentStatusId == Paid"
							required></v-autocomplete>
						<date-picker
							label="Invoice Date"
							v-model="invoice.invoiceDate"></date-picker>
					</v-container>
				</v-card-text>
				<v-card-text class="py-0 mb-2">
					<v-container
						class="d-flex justify-space-between flex-wrap flex-md-nowrap flex-lg-nowrap">
						<v-container>
							<v-autocomplete
								:rules="[(v) => !!v || 'Type is required']"
								rounded
								outlined
								dense
								label="Invoice Type"
								:items="invoiceTypes"
								item-text="displayName"
								item-value="id"
								@change="
									$refs['form'].validate();
									invoice.associatedId = null;
								"
								:disabled="paymentStatusId == Paid"
								v-model="invoice.typeId"></v-autocomplete>
							<v-autocomplete
								:rules="[(v) => !!v || 'Customer is required']"
								rounded
								outlined
								dense
								label="Customer"
								item-value="id"
								:items="customers"
								:item-text="getCustomerName"
								v-model="invoice.customerId"
								required>
								<template v-slot:no-data>
									<v-card elevation="0">
										<v-card-text class="text-center">
											<h4 class="mb-2">
												Customer not found
											</h4>
											<v-btn
												depressed
												small
												rounded
												color="primary"
												@click="
													customerDialog =
														!customerDialog
												">
												<v-icon class="mr-2" small>
													mdi-plus
												</v-icon>
												Add Customer
											</v-btn>
										</v-card-text>
									</v-card>
								</template>
							</v-autocomplete>
						</v-container>
						<v-container>
							<v-autocomplete
								:rules="[
									(v) => {
										if (invoice.typeId == general || !!v)
											return true;
										else if (invoice.typeId && !v)
											return 'Required';
										return true;
									},
								]"
								rounded
								outlined
								dense
								:items="items"
								item-value="id"
								item-text="id"
								label="Id"
								v-model="invoice.associatedId"
								:disabled="paymentStatusId == Paid"
								required></v-autocomplete>

							<v-text-field
								:rules="[
									(v) =>
										v > 0 ||
										'Total Amount Due must be greater than 0',
									(v) =>
										!!v || 'Total Amount Due is required',
								]"
								:prefix="currencySymbol"
								rounded
								outlined
								dense
								label="Total Amount Due"
								:disabled="paymentStatusId == Paid"
								v-model="invoice.totalAmount"></v-text-field>
						</v-container>
					</v-container>
				</v-card-text>

				<v-card-text class="py-0 mb-2">
					<v-container>
						<v-textarea
							rounded
							outlined
							label="Description"
							v-model="invoice.description"></v-textarea>
					</v-container>
				</v-card-text>
			</v-form>
		</v-card>
		<v-card elevation="0" min-width="50%" class="rounded-xl">
			<v-card-text v-if="isUpdating" class="py-0 mt-2 mb-2">
				<v-container class="py-0">
					<v-container
						class="d-flex justify-space-between align-center">
						<v-container class="pa-0">
							<div class="form-section-title">
								Payment Details
							</div>
						</v-container>
						<v-container
							class="d-flex justify-end align-center flex-wrap flex-md-nowrap flex-lg-nowrap pa-0">
							<v-btn
								color="warning"
								depressed
								rounded
								@click="paymentDialog = true"
								:loading="isPaymentLoading"
								:disabled="paymentStatusId == this.Paid">
								<v-icon class="mr-2">mdi-cash</v-icon>
								Make Payment
							</v-btn>
						</v-container>
					</v-container>
					<payment-list
						v-if="payments.length > 0"
						v-model="payments"
						:show-title="false"
						hide-filters
						hide-footer
						:custom-headers="[
							'id',
							'amount',
							'credited',
							'typeId',
							'paymentDate',
							'createdBy',
							'action',
						]"
						@delete="handlePaymentRemoved"></payment-list>
				</v-container>
			</v-card-text>
			<v-card-text class="py-0">
				<v-container class="pa-4">
					<!-- Header Section -->
					<div class="d-flex align-center justify-space-between mb-3">
						<div class="d-flex align-center">
							<div class="form-section-title">
								Charge List
							</div>
						</div>
						<v-chip small color="primary" outlined>
							{{ invoiceDetails.length }}
							{{ invoiceDetails.length === 1 ? "item" : "items" }}
						</v-chip>
					</div>

					<!-- Empty State -->
					<v-container
						v-if="invoiceDetails.length === 0"
						class="text-center py-8">
						<v-icon size="64" color="grey lighten-1">
							mdi-package-variant-closed
						</v-icon>
						<p class="text-subtitle-1 grey--text mt-3">
							No items added yet
						</p>
						<p class="text-caption grey--text">
							Click the button below to add your first item
						</p>
					</v-container>
					<!-- Charge Items -->
					<v-card
						v-for="(invoiceDetail, idx) in invoiceDetails"
						:key="`invoice-detail-${idx}`"
						class="mb-3 charge-item-card rounded-xl"
						:class="{
							'inactive-item':
								invoiceDetail.id && !invoiceDetail.isActive,
						}"
						outlined
						elevation="0">
						<v-card-text class="pa-3">
							<div class="d-flex align-center mb-2">
								<v-spacer></v-spacer>
								<!-- Delete/Undo Actions -->
								<v-btn
									v-if="!isUpdating || !invoiceDetail.id"
									color="error"
									icon
									small
									@click="deleteInvoiceDetail(idx)">
									<v-icon small>mdi-close</v-icon>
								</v-btn>
								<v-btn
									v-if="
										invoiceDetail.id &&
										invoiceDetail.isActive &&
										isUpdating
									"
									color="error"
									icon
									small
									@click="removeInvoiceDetail(idx)"
									:disabled="paymentStatusId == Paid">
									<v-icon small>mdi-close</v-icon>
								</v-btn>
								<v-btn
									v-if="
										invoiceDetail.id &&
										!invoiceDetail.isActive &&
										isUpdating
									"
									color="success"
									icon
									small
									@click="undoRemoveInvoiceDetail(idx)">
									<v-icon small>mdi-undo</v-icon>
								</v-btn>
							</div>

							<v-row dense>
								<!-- Item Name - Full Width Row -->
								<v-col cols="12">
									<v-text-field
										:label="`Item Name`"
										dense
										rounded
										outlined
										hide-details="auto"
										v-model="invoiceDetail.name"
										:disabled="
											paymentStatusId == Paid
										"></v-text-field>
								</v-col>

								<!-- Quantity and Unit Price Row -->
								<v-col
									cols="6"
									:md="invoice.typeId == Order ? 4 : 6">
									<v-text-field
										label="Quantity"
										dense
										rounded
										outlined
										hide-details="auto"
										v-model.number="invoiceDetail.quantity"
										@change="updateTotalAmount"
										:disabled="paymentStatusId == Paid"
										type="number"></v-text-field>
								</v-col>

								<v-col
									cols="6"
									:md="invoice.typeId == Order ? 4 : 6">
									<v-text-field
										:prefix="currencySymbol"
										label="Unit Price"
										dense
										rounded
										outlined
										hide-details="auto"
										v-model.number="invoiceDetail.unitPrice"
										@change="updateTotalAmount"
										:disabled="paymentStatusId == Paid"
										type="number"></v-text-field>
								</v-col>

								<!-- Case Quantity (conditional) -->
								<v-col
									v-if="invoice.typeId == Order"
									cols="12"
									md="4">
									<v-text-field
										label="Case"
										dense
										rounded
										outlined
										hide-details="auto"
										v-model.number="
											invoiceDetail.caseQuantity
										"
										:disabled="paymentStatusId == Paid"
										type="number"></v-text-field>
								</v-col>
							</v-row>

							<!-- Subtotal Display -->
							<div
								class="d-flex justify-end mt-2"
								v-if="
									invoiceDetail.quantity &&
									invoiceDetail.unitPrice
								">
								<v-chip
									small
									color="grey lighten-3"
									class="font-weight-medium">
									<v-icon x-small left>mdi-calculator</v-icon>
									Subtotal: {{ currencySymbol
									}}{{
										(
											invoiceDetail.quantity *
											invoiceDetail.unitPrice
										).toFixed(2)
									}}
								</v-chip>
							</div>
						</v-card-text>
					</v-card>

					<!-- Add Button -->
					<v-container class="d-flex justify-center pa-0 mt-4">
						<v-btn
							color="primary"
							depressed
							rounded
							@click="addInvoiceDetail"
							:disabled="paymentStatusId == Paid">
							<v-icon left small>mdi-plus</v-icon>
							Add Item
						</v-btn>
					</v-container>
				</v-container>
			</v-card-text>
		</v-card>
		<general-dialog
			:dialog="deleteDialog"
			:options="deleteOptions"
			v-model="deleteDialog"
			@handle-event="deleteInvoice"></general-dialog>

		<customer-dialog v-model="customerDialog"></customer-dialog>

		<payment-dialog
			v-if="paymentDialog"
			v-model="paymentDialog"
			:invoice="invoice"
			:is-locked="true"
			@handle-event="handlePaymentAdded"></payment-dialog>
	</v-container>
</template>
<script>
import eventBus from "@/js/event-bus";
import backendService from "@/services/backend-service";
import generalMixin from "@/js/general-mixin.js";
import PaymentDialog from "@/components/PaymentDialog.vue";
import PaymentList from "@/components/PaymentList.vue";
import GeneralDialog from "@/components/GeneralDialog.vue";
import CustomerDialog from "@/components/CustomerDialog.vue";
import Decimal from "decimal.js";
import DatePicker from "@/components/DatePicker.vue";
import {
	InvoiceType,
	PaymentStatus,
	CurrencyType,
	ShipmentType,
} from "@/js/enum";
export default {
	name: "InvoiceForm",
	mixins: [generalMixin],
	components: {
		"payment-dialog": PaymentDialog,
		"payment-list": PaymentList,
		"general-dialog": GeneralDialog,
		"customer-dialog": CustomerDialog,
		"date-picker": DatePicker,
	},
	mounted: function () {
		if (this.$route.params.id) {
			this.getUpdateForm();
		} else if (this.isGenerating) {
			if (this.type == this.getIdByEnum(InvoiceType.Shipment)) {
				this.invoice.customerId = this.shipment.senderId;
				this.invoice.typeId = this.getIdByEnum(InvoiceType.Shipment);
				this.currencyId = this.getIdByEnum(CurrencyType.UsDollar);
				this.invoice.associatedId = this.shipment.id;
				this.invoice.mtlTracking = this.shipment.mtlTracking;
				let invoiceDetail = {
					name: `${this.getOptionName(
						this.shipment.typeId
					)} - Shipment ${this.shipment.id}`,
					quantity: this.shipment.totalWeight,
					unitPrice: 2.75,
					isActive: true,
				};
				if (
					this.shipment.typeId == this.getIdByEnum(ShipmentType.Sea)
				) {
					invoiceDetail.quantity = this.shipment.totalPallet;
					invoiceDetail.unitPrice = 1000;
				}
				this.invoiceDetails.push(invoiceDetail);
			} else if (this.type == this.getIdByEnum(InvoiceType.Order)) {
				this.invoice.typeId = this.type;
				this.invoice.associatedId = this.order.id;
				this.invoice.mtlTracking = this.order.mtlTracking;
				this.order.orderDetails.forEach((orderDetail) => {
					let invoiceDetail = {
						name: orderDetail.productTitle,
						quantity: orderDetail.quantity,
						unitPrice: orderDetail.unitPrice,
						caseQuantity: orderDetail.caseQuantity,
						isActive: true,
					};
					this.invoiceDetails.push(invoiceDetail);
				});
			} else {
				if (this.customerId) {
					this.invoice.customerId = this.customerId;
				}
			}

			this.invoice.mtlTracking = this.shipment.mtlTracking;
			this.updateTotalAmount();
		} else {
			this.currencyId = this.getIdByEnum(CurrencyType.UsDollar);
		}
		this.$refs["form"].validate();
	},
	props: {
		shipment: {
			type: Object,
			default: () => {
				return {};
			},
		},
		type: {
			type: Number,
			default: null,
		},
		order: {
			type: Object,
			default: () => {
				return {};
			},
		},
		isGenerating: {
			type: Boolean,
			default: false,
		},
		customerId: {
			type: Number,
			default: null,
		},
	},
	data() {
		return {
			deleteDialog: false,
			customerDialog: false,
			paymentDialog: false,
			cashDialog: false,
			digitalDialog: false,
			originalInvoice: {},
			isUpdating: false,
			isPaymentLoading: false,
			isLoading: false,
			isDeleteLoading: false,
			invoiceDetails: [],
			deleteOptions: {
				btnText: "Yes",
				message: "This action is not revertable!",
				title: "Are you sure you want to delete this invoice?",
			},
			paymentStatusId: null,
			invoice: {
				typeId: null,
				customerId: null,
				associatedId: null,
				description: "",
				totalAmount: 0,
				payment: {},
				paymentStatusId: null,
				currencyId: null,
			},
			currencyId: null,
			totalAmount: 0,
			formValid: false,
			paymentFormValid: false,
			payment: { statusId: null, typeId: null, paymentDate: null },
			payments: [],
			dropdownRules: [
				(v) => {
					if (v) return true;
					return "Required";
				},
			],
		};
	},
	computed: {
		amountPaid() {
			if (this.invoice && this.invoice.payments) {
				let sum = 0;
				let payments = this.invoice.payments;
				for (let i = 0; i < payments.length; i++) {
					sum += payments[i].amount;
				}
				return sum;
			}
			return 0;
		},
		currencySymbol() {
			let us = this.getIdByEnum(CurrencyType.UsDollar);
			let viet = this.getIdByEnum(CurrencyType.VietDong);
			if (this.currencyId == us) {
				return "$";
			} else if (this.currencyId == viet) {
				return "₫";
			}
			return "";
		},
		currencyTypes() {
			if (this.$store.getters["dropdownToOptions"]["currency"])
				return this.$store.getters["dropdownToOptions"]["currency"]
					.options;
			return [];
		},
		customers() {
			return this.$store.getters["customers"];
		},
		shipments() {
			return this.$store.getters["shipments"];
		},
		items() {
			if (this.invoice.typeId == this.getIdByEnum(InvoiceType.Shipment))
				return this.shipments;
			else if (this.invoice.typeId == this.getIdByEnum(InvoiceType.Order))
				return this.orders;

			return [];
		},
		Paid() {
			return this.getIdByEnum(PaymentStatus.Paid);
		},
		PartialPaid() {
			return this.getIdByEnum(PaymentStatus.PartialPaid);
		},
		general() {
			return this.getIdByEnum(InvoiceType.General);
		},
		orders() {
			return this.$store.getters["orders"];
		},
		invoiceTypes() {
			if (
				this.$store.getters["dropdownToOptions"] &&
				this.$store.getters["dropdownToOptions"]["invoiceType"]
			)
				return this.$store.getters["dropdownToOptions"]["invoiceType"]
					.options;
			return [];
		},
		paymentStatuses() {
			if (
				this.$store.getters["dropdownToOptions"] &&
				this.$store.getters["dropdownToOptions"]["paymentStatus"]
			)
				return this.$store.getters["dropdownToOptions"]["paymentStatus"]
					.options;
			return [];
		},
		paymentTypes() {
			if (
				this.$store.getters["dropdownToOptions"] &&
				this.$store.getters["dropdownToOptions"]["paymentType"]
			)
				return this.$store.getters["dropdownToOptions"]["paymentType"]
					.options;
			return [];
		},
		paymentDate() {
			if (this.invoice.payment && this.invoice.payment.dateCreated)
				return this.convertToDate(this.invoice.payment.dateCreated);
			return "";
		},
		Order() {
			return this.getIdByEnum(InvoiceType.Order);
		},
		Shipment() {
			return this.getIdByEnum(InvoiceType.Shipment);
		},
	},
	methods: {
		addInvoiceDetail() {
			this.invoiceDetails.push({
				isActive: true,
				name: "",
				unitPrice: null,
				quantity: null,
				caseQuantity: null,
				lineTotal: 0,
			});
		},
		addInvoice() {
			if (!this.$refs.form.validate()) return;
			this.isLoading = true;
			this.invoice.currencyId = this.currencyId;
			this.invoice.invoiceDetails = this.invoiceDetails;
			backendService
				.addInvoice(this.invoice)
				.then((response) => {
					this.timer(1000).then(() => {
						this.invoice = response.data;
						this.invoiceDetails = this.invoice.invoiceDetails;
						this.isLoading = false;
						this.isUpdating = true;
						this.$router.push({
							name: "InvoiceUpdate",
							params: { id: this.invoice.id },
						});
						this.successSnackbar(
							"Successfully added a new invoice"
						);
					});
				})
				.catch((err) => {
					let message = err.response.data;
					this.timer(1000).then(() => {
						if (
							typeof message == "string" &&
							message.includes("Duplicate")
						) {
							this.errorSnackbar(
								"Repeated tracking # under invoice list"
							);
						} else {
							this.errorSnackbar("Ops! Something is not right");
						}
						this.isLoading = false;
					});
				});
		},
		deleteInvoice() {
			this.isDeleteLoading = true;
			backendService
				.deleteInvoiceById(this.invoice.id)
				.then(() => {
					setTimeout(() => {
						this.isDeleteLoading = false;
						this.$router.go(-1);
						eventBus.$emit(
							"setSnackbar",
							"Successfully delete the invoice",
							"success",
							true
						);
					}, 1000);
				})
				.catch(() => {
					this.isDeleteLoading = false;
					eventBus.$emit(
						"setSnackbar",
						"Ops! Something is not right!",
						"error",
						true
					);
				}, 1000);
		},
		deleteInvoiceDetail(idx) {
			this.invoiceDetails.splice(idx, 1);
		},
		getCustomerName(item) {
			return `${item.id} - ${item.name} - ${item.phone}`;
		},
		getUpdateForm() {
			this.isUpdating = true;
			this.getInvoiceById(this.$route.params.id);
		},
		getInvoiceById(id) {
			backendService.getInvoiceById(id).then((response) => {
				this.invoice = response.data;
				this.currencyId = this.invoice.currencyId;
				this.invoiceDetails = this.invoice.invoiceDetails;
				this.originalInvoice = JSON.stringify(this.invoice);
				this.paymentStatusId = this.invoice.paymentStatusId;
				this.payments = this.normalizePayments(this.invoice.payments);
				this.invoice.payments = this.payments;
			});
		},
		handlePaymentAdded(val) {
			this.payments.push(this.normalizePayment(val));
			this.invoice.payments = this.payments;
			if (this.amountPaid >= this.invoice.totalAmount) {
				this.invoice.paymentStatusId = this.Paid;
				this.paymentStatusId = this.Paid;
			} else if (this.amountPaid < this.invoice.totalAmount) {
				this.invoice.paymentStatusId = this.PartialPaid;
				this.paymentStatusId = this.PartialPaid;
			}
		},
		handlePaymentRemoved(id) {
			this.payments = this.payments.filter((e) => e.id != id);
			this.invoice.payments = this.payments;
			if (this.amountPaid == this.invoice.totalAmount) {
				this.invoice.paymentStatusId = this.Paid;
				this.paymentStatusId = this.Paid;
			} else if (this.amountPaid < this.invoice.totalAmount) {
				this.invoice.paymentStatusId = this.PartialPaid;
				this.paymentStatusId = this.PartialPaid;
			}
		},
		normalizePayments(payments) {
			if (!payments) return [];
			return payments.map((payment) => this.normalizePayment(payment));
		},
		normalizePayment(payment) {
			return {
				...payment,
				accountId: payment.accountId || this.invoice.customerId,
				currencyId: payment.currencyId || this.invoice.currencyId,
				invoiceId: payment.invoiceId || this.invoice.id,
			};
		},
		undoRemoveInvoiceDetail(idx) {
			this.invoiceDetails[idx].isActive = true;
			this.updateTotalAmount();
		},
		removeInvoiceDetail(idx) {
			this.invoiceDetails[idx].isActive = false;
			this.updateTotalAmount();
		},
		updateInvoice() {
			this.isLoading = true;
			this.invoice.currencyId = this.currencyId;
			this.invoice.invoiceDetails = this.invoiceDetails;
			backendService
				.updateInvoice(this.invoice)
				.then((response) => {
					this.timer(1000).then(() => {
						this.invoice = response.data;
						this.invoiceDetails = this.invoice.invoiceDetails;
						this.originalInvoice = JSON.stringify(this.invoice);
						this.isLoading = false;
						this.successSnackbar("Successfully updated invoice");
					});
				})
				.catch((err) => {
					let message = err.response.data;
					this.timer(1000).then(() => {
						if (
							typeof message == "string" &&
							message.includes("Duplicate")
						) {
							this.errorSnackbar(
								"Repeated tracking # under invoice list"
							);
						} else {
							this.errorSnackbar("Ops! Something is not right");
						}
						this.isLoading = false;
					});
				});
		},
		updateTotalAmount() {
			this.invoice.totalAmount = this.invoiceDetails.reduce((ps, a) => {
				if (a.isActive)
					return new Decimal(ps).plus(
						new Decimal(a.unitPrice).times(new Decimal(a.quantity))
					);
				return ps;
			}, 0);
		},
	},
	watch: {
		"$route.name": {
			handler: function (name) {
				if (name == "NewInvoice") {
					this.isLoading = false;
					this.isUpdating = false;
					this.totalAmount = 0;
					this.invoiceDetails = [];
					this.paymentStatusId = null;
					this.invoice = {
						typeId: null,
						customerId: null,
						associatedId: null,
						description: "",
						totalAmount: 0,
						payment: {},
						paymentStatusId: null,
					};
				}
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>
<style scoped>
.invoice-form-page {
	gap: 8px;
}
.invoice-form-page :deep(.v-card__title) {
	padding: 16px 16px 12px;
}
.invoice-form-page :deep(.v-card__text) {
	margin-bottom: 0 !important;
	padding: 0 16px 8px !important;
}
.invoice-form-page :deep(.v-card__text > .container:not(.pa-4)),
.invoice-form-page :deep(.v-card__text > .container-fluid:not(.pa-4)) {
	max-width: none;
	padding: 0 !important;
}
.invoice-form-page :deep(.v-card__text > .container.d-flex:not(.pa-4)),
.invoice-form-page :deep(.v-card__text > .container-fluid.d-flex:not(.pa-4)) {
	gap: 8px;
}
.invoice-form-page :deep(.v-card__text > .container > .container),
.invoice-form-page :deep(.v-card__text > .container-fluid > .container) {
	padding-left: 0;
	padding-right: 0;
}
.charge-item-card {
	transition: all 0.2s ease-in-out;
	border-radius: 8px;
}

.charge-item-card:hover {
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.inactive-item {
	opacity: 0.6;
	background-color: #f5f5f5;
}
</style>
